<template>
  <div :id="id + '_fs'" :class="_fs_class">
    <cat-label
      v-if="!labelHandled"
      :label-id="id + '_lbl'"
      :for-id="id"
      :label="label"
      :optional="!mandatory"
      :placeholder="offscreenLabel"
      :errors="errors"
      :readonly="readonly"
      :inline="help && !!help.url"
    ></cat-label>
    <cat-help-trigger
      v-if="help && help.url"
      :source-id="id"
      :url="help.url"
      :title="label"
    />
    <cat-hint :id="id + '_hint'" :text="hint" />
    <slot></slot>
    <cat-help
      v-if="help && help.text"
      :id="id"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_lbl' : null"
    />
    <div :class="_holder_class">
      <span class="dropdown-indicator" tabindex="-1" aria-hidden="true"></span>
      <label v-if="labelHandled && label" :for="id" class="placeholder">{{
        label
      }}</label>
      <select
        :id="id"
        v-model="model"
        :aria-required="mandatory"
        :required="errors && errors.length > 0"
        :aria-invalid="errors && errors.length > 0 ? 'true' : null"
        :readonly="readonly"
        :disabled="disabled"
        :aria-disabled="disabled ? 'true' : null"
        :aria-labelledby="labelHandled && label ? null : id + '_lbl'"
        :aria-describedby="hint ? id + '_hint' : null"
        :aria-label="labelHandled && !label ? offscreenLabel : null"
        :class="errors && errors.length > 0 ? 'invalidField' : ''"
        @change="_onChange"
        @blur="_onBlur"
      >
        <option v-if="!suppressDefaultOption" value>
          {{ placeholderMessage }}
        </option>
        <option
          v-for="(item, idx) in items"
          :key="idx"
          :value="item.value"
          :selected="item.value == value ? 'selected' : ''"
        >
          {{ item.text }}
        </option>
      </select>
    </div>
    <slot name="after-input"></slot>
    <cat-errors v-if="errors && errors.length > 0" :errors="errors" />
    <div role="presentation" class="spacer"></div>
  </div>
</template>

<script lang="ts">
import jQuery from "jquery";
// import "jquery-ui";
// TODO: jquery-ui - we use for :focusable elements
// require("jquery-ui-dist/jquery-ui");

import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger";
import CatHint from "@/components/utilities/hint.vue";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatHelp from "@/components/utilities/help.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import SelectMixin from "./select-mixin.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatAttributeSelect",
  components: {
    CatErrors,
    CatHelp,
    CatLabel,
    CatHelpTrigger,
    CatHint
  },
  mixins: [SelectMixin],
  props: {
    id: [String],
    errors: [Array],
    label: [String],
    items: [Array],
    hint: [String],
    placeholder: [String],
    suppressDefaultOption: {
      type: Boolean,
      default: false
    },
    offscreenLabel: [String],
    labelHandled: {
      type: Boolean,
      default: false,
      description: "Renders a placeholder label if not handled"
    },
    value: [String],
    mandatory: {
      type: Boolean,
      default: false
    },
    size: [String],
    readonly: [Boolean],
    disabled: [Boolean],
    help: [Object]
  },
  data: function () {
    return {
      model: this.value ? this.value : ""
    };
  },
  computed: {
    _fs_class: function () {
      var css = {
        fieldset: true,
        "cat-select": true,
        invalid:
          this.errors &&
          this.errors.length > 0 &&
          this.errors.some(function (r) {
            return r.level <= 1;
          }),
        required: this.mandatory,
        disabled: this.disabled,
        optional: !this.mandatory,
        "readonly-list": this.readonly
      };
      css.warning =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 2 && !css.invalid;
        });
      css.infomessage =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 3 && !css.invalid;
        });
      css.debugmessage =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 4;
        });
      return css;
    },
    _holder_class: function () {
      return {
        // 'select-holder': !this.autocomplete,
        "select-holder": true,
        small: this.size === "small",
        medium: this.size === "medium",
        large: this.size === "large",
        xlarge: this.size === "xlarge"
      };
    },
    placeholderMessage: function () {
      if (this.placeholder) {
        return this.placeholder;
      }
      if (this.mandatory) {
        return this.resolveTxt("skin.please.select.mandatory");
      }
      return this.resolveTxt("skin.please.select");
    }
  },
  watch: {
    value: function (newval, oldvalue) {
      const nv = newval ? newval : "";
      if (this.model !== nv) {
        this.model = nv;
        if (nv !== "") {
          jQuery("#" + this.id).trigger("change");
        }
      }
    }
  },
  methods: {
    _onBlur: function (event) {
      this.$emit("ui-blur", {
        event: event,
        id: this.id
      });
    }
  }
});
</script>
